import type { FilterCondition, FilterGroup, GroupLogic } from './filter';
import { CONTACT_SIGNAL_FIELD_KEYS } from './contact-view';
import { NO_VALUE_OPERATORS } from './filter-operators';
import { CAMPAIGN_SIGNAL_NAME_INVARIANT_PREFIX } from '../schemas/terra-signals';

/**
 * The Signal-narrowing rule (Item A) — SHARED between server and web (U4,
 * plan `plans/2026-09-24-005-plays-tab-one-pr-plan.md`).
 *
 * This module is deliberately free of `zod` and `Prisma`: it is imported by
 * `web/app-home/src/components/flow/contact/ContactFlowTable.tsx` (U5) as
 * well as the backend (`src/services/flow/query/contact-signal-columns.ts`),
 * so it can only depend on plain TS types and other zero-dependency
 * `src/shared/flow` modules.
 *
 * Originally one server-only module (`contact-signal-columns.ts`'s
 * `hasNarrowingSignalCondition` guard, U3, plan 002 lines 342-358, Decision
 * 8). Moved here so the frontend's "Pick which play first" prompt (U5) can
 * decide, from the SAME rule, whether the rep's current filters already
 * narrow to one play — without duplicating the guard's degenerate-value
 * handling (see `conditionValueNarrowsSignals` below) a second time in web
 * code.
 *
 * A caller identifies "is this field a Signal field" differently depending
 * on what it has on hand: the server has a `FilterFieldMap` (its own
 * `isTerraSignalsField(fieldMap, key)` predicate); the web client has no
 * field map, only `CONTACT_SIGNAL_FIELD_KEYS`. Every exported function here
 * therefore takes an `isSignalField` predicate, defaulting to
 * `defaultIsSignalField` (the `CONTACT_SIGNAL_FIELD_KEYS` form) — the server
 * wrapper (`hasNarrowingSignalCondition` in `contact-signal-columns.ts`)
 * passes its own field-map-backed predicate instead of relying on the
 * default.
 */
export type IsSignalField = (field: string) => boolean;

/** `CONTACT_SIGNAL_FIELD_KEYS`-backed default — what every caller without its own field map should use. */
export const defaultIsSignalField: IsSignalField = (field) => CONTACT_SIGNAL_FIELD_KEYS.includes(field);

/**
 * Signal fields that are `filterOnly` on the CONTACT catalog (never a
 * display column, so never in `CONTACT_SIGNAL_FIELD_KEYS` /
 * `defaultIsSignalField` — see `contact-view.ts`'s `CONTACT_SIGNAL_FIELD_KEYS`
 * doc comment) but ARE still a Signal-scoped filter a rep can put in a
 * group — so `countSignalConditionGroups`'s "is a Signal predicate present
 * here" question (D9, Item C) needs to see them even though "is this a
 * visible Signal column" (`defaultIsSignalField`) correctly does not.
 *
 * Must mirror the `filterOnly` keys in `CONTACT_SIGNAL_FILTER_FIELDS`
 * (`filter-fields.ts`) — that catalog is the source of truth for which keys
 * are Signal-scoped filters at all; this list is only the subset
 * `defaultIsSignalField` misses. Pinned by a drift test in
 * `signal-narrowing.test.ts` (every `CONTACT_SIGNAL_FILTER_FIELDS` key
 * satisfies `defaultIsSignalConditionField`).
 */
const SIGNAL_FILTER_ONLY_FIELD_KEYS = ['campaign_playhouse_visible'] as const;

/**
 * `IsSignalField` for `countSignalConditionGroups`' default: every visible
 * Signal column (`CONTACT_SIGNAL_FIELD_KEYS`) plus the `filterOnly` Signal
 * fields (`SIGNAL_FILTER_ONLY_FIELD_KEYS`) that `defaultIsSignalField` alone
 * would miss.
 */
export const defaultIsSignalConditionField: IsSignalField = (field) =>
  CONTACT_SIGNAL_FIELD_KEYS.includes(field) || (SIGNAL_FILTER_ONLY_FIELD_KEYS as readonly string[]).includes(field);

/**
 * The two Signal fields whose value pins a request to a SPECIFIC play —
 * the only Signal conditions that actually narrow the per-row `LEFT JOIN
 * LATERAL`'s input (U3, plan 002 lines 342-358, Decision 8). Every OTHER
 * `terra_signals`-scoped field (`signal_type`, `signal_status`,
 * `has_responded`, `signal_status_rank`, `signal_action_state`,
 * `signal_source_system`, `signal_invitee_type`,
 * `campaign_playhouse_visible`) matches a large fraction of the 246k
 * `terra_signals` rows and does NOT bound the lateral. Three measurements
 * on the prod read-only replica (2026-09-15) drove the split:
 *   - B2 (alumni filter only, `no_longer_at_company equals false` — passes
 *     TODAY's "any filter counts" guard): 25,051ms even filtered.
 *   - B4 (`signal_status_rank gt 0`, a `terra_signals` field NOT in this
 *     list): 5,722ms, 113,870 LATERAL probes.
 *   - B5 (`signal_name contains '<play>'`, IN this list): 1,184ms, 18,492
 *     probes — the one shape that is actually selective.
 * `search` never counts either, for the same reason it never did: a
 * one-character substring matches most of the mirror.
 */
export const SIGNAL_NARROWING_FIELDS: readonly string[] = ['signal_name', 'signal_subtype'];

/**
 * Operators that pin a Signal-narrowing field's value to something
 * specific. Deliberately excludes every negation (`not_equals`,
 * `not_contains`) and both emptiness checks (`is_empty`, `is_not_empty`) —
 * each of those matches MOST rows, the opposite of narrowing.
 *
 * Field + operator alone is NOT sufficient, though — `cond.value` still has
 * to actually compile to a narrowing predicate. `condition-to-sql.ts`
 * degenerates in two ways this module must reject even though field+operator
 * look fine: `value: null` makes `buildPredicate` return `null`
 * (`condition-to-sql.ts:305`), so `conditionToSql` drops the condition
 * entirely — the compiled WHERE narrows nothing, reopening B2 (25,051ms).
 * `value: ''` for `contains` is NOT caught by that `null` check, so it
 * compiles to `ILIKE likeContainsPattern('')` = `ILIKE '%%'`, matching every
 * non-null row — the documented B4 shape (5,722ms / 113,870 LATERAL probes).
 *
 * A THIRD degenerate shape, and the one that is not obvious: `signal_name` is
 * CONSTRUCTED by the arms, not stored. The Campaign arm (246k of the 246k
 * snapshot rows) emits
 * `concat_ws(' · ', 'Campaign', c.type, c.name, t.status)`, whose first
 * argument is a literal and whose `c.name` is non-null — so EVERY row's
 * `signal_name` begins with `'Campaign · '`
 * (`CAMPAIGN_SIGNAL_NAME_INVARIANT_PREFIX`, exported and doc-derived by the
 * arm itself). A `contains` value that is a case-insensitive substring of that
 * prefix — `' · '`, `'Campaign'`, `'Camp'`, `'ampaig'`, `'a'`, `'i'` — matches
 * the entire arm by construction, so the compiled `ILIKE` bounds the Signal
 * LATERAL by nothing and reopens B2 (25,051ms).
 *
 * This is a DERIVATION from the emitted SQL, not a minimum-length floor (a
 * floor was proposed and rejected: it is the wrong predicate — `'Z'` is
 * selective at 1 char, `'ampaig'` is not at 6) and not a hand-typed blocklist
 * (which would drift the moment someone edits the `concat_ws`). It is applied
 * to `signal_name` ONLY: `signal_subtype` is `c.name` / the play name with no
 * scaffolding, and rejecting `'Camp'` there would wrongly refuse a play named
 * "Campus Tour". `equals` and `in_any_of` compile to exact matches rather than
 * substring scans, so they need no such check.
 *
 * RESIDUAL, deliberately shipped (open question resolved 2026-09-15): values
 * that are not part of the scaffolding but are still unselective — `'e'`,
 * `'Event'`, `'Invited'` — still pass. Those are the B4 class (~5.7s
 * measured), which the 8s signals envelope bounds to a slow list and one
 * occupied slot of four, not an outage. The two follow-up options, both
 * declined for now: (A) a cardinality pre-probe, which puts an extra filtered
 * scan on every Signal-sorted request and needs a replica measurement; (B) a
 * product rule against free-text `contains`, which is exactly how the UI's
 * play filter works today (the measured B5 shape) and would break the rep flow
 * this guard exists to keep fast.
 *
 * `conditionValueNarrowsSignals` below is the value-shape half of the guard;
 * `NARROWING_OPERATORS` only decides which operators are even eligible.
 */
export const NARROWING_OPERATORS: ReadonlySet<string> = new Set(['equals', 'contains', 'in_any_of']);

/**
 * True when `cond.value` itself compiles to a predicate that actually
 * narrows — the check `SIGNAL_NARROWING_FIELDS`/`NARROWING_OPERATORS` alone
 * cannot make, because both only look at field + operator (see the
 * `NARROWING_OPERATORS` comment above for the two degenerate shapes this
 * closes: `ILIKE '%%'` and a dropped condition).
 *
 * Also the value-blankness half of `countSignalConditionGroups` (D9, Item
 * C): a Signal-field condition whose value is blank per this rule does not
 * count toward the split-group warning either, for the same reason it does
 * not narrow.
 */
export function conditionValueNarrowsSignals(cond: FilterCondition): boolean {
  if (cond.operator === 'in_any_of') {
    return Array.isArray(cond.value) && cond.value.length > 0;
  }
  // `equals` / `contains`: a null/undefined value either compiles to nothing
  // (dropped condition, B2) or — for `contains` — is caught by the blank
  // check below anyway. Trim so `'   '` (whitespace-only) is also blank.
  if (cond.value == null) return false;
  const trimmed = String(cond.value).trim();
  if (trimmed.length === 0) return false;
  // The arm-scaffolding shape (see the `NARROWING_OPERATORS` comment): a
  // `contains` value that is a case-insensitive substring of the Campaign
  // arm's invariant `signal_name` prefix matches every row the arm emits.
  // Compared against the UNTRIMMED value, because `condition-to-sql.ts` builds
  // the ILIKE pattern from the untrimmed value too — `' Campaign'` really does
  // search for a leading space, which the prefix does not contain, so it
  // narrows. `trimmed` above is only the blankness test.
  if (cond.operator === 'contains' && cond.field === 'signal_name') {
    return !CAMPAIGN_SIGNAL_NAME_INVARIANT_PREFIX.toLowerCase().includes(
      String(cond.value).toLowerCase(),
    );
  }
  return true;
}

/**
 * True when `cond` compiles to a real predicate at all — mirrors what
 * `condition-to-sql.ts` actually compiles (U5, 2-2; corrected codex r2 —
 * partial fix of finding 2-2): `NO_VALUE_OPERATORS` (`is_empty` /
 * `is_not_empty` / `before_today`) carry no value and always compile
 * (`buildPredicate`'s universal short-circuit, ahead of every other check);
 * every other operator compiles as long as `cond.value` is non-null
 * (`buildPredicate`'s `if (value == null) return null;` gate — the ONLY
 * other universal drop rule, run before any type-specific branch).
 *
 * Read literally off `buildPredicate` for the two shapes that look like they
 * should also be "blank" but are NOT, per the compiler that actually runs
 * (verified against `condition-to-sql.ts` directly, not re-derived from
 * memory):
 *  - `contains '   '` / `not_contains ''` / `equals ''` all compile to a
 *    real (if unselective) predicate — `ILIKE '%   %'` or
 *    `LOWER(col) = LOWER('')` — never `null`. A previous version of this
 *    function trimmed and blank-checked the value here, which is WRONG:
 *    the compiler has no such check, so a whitespace-only or empty-string
 *    value still produces a real Signal predicate. This under-counted the
 *    split-group warning (`countSignalConditionGroups`): a group whose only
 *    Signal condition was e.g. `signal_subtype not_contains " "` was
 *    wrongly treated as inactive, so a genuine two-group split (paired with
 *    another group's `signal_subtype = "Campus Tour"`) counted as 1, not 2,
 *    and the warning never fired (codex r2 finding, partial fix of 2-2).
 *  - `in_any_of []` / `not_in_any_of []` also compile to a real predicate —
 *    `FALSE` / `TRUE` respectively (`MATCH_NOTHING` / `MATCH_EVERYTHING` in
 *    `condition-to-sql.ts`) — never `null`. A previous version required a
 *    non-empty array here too; same under-count risk, same fix.
 *
 * RESIDUAL, deliberately not chased here (matches the file's own house
 * style for documenting an accepted gap rather than silently leaving it):
 * a `type: 'number'` field (Signal's only one is `signal_status_rank`) whose
 * value does not coerce to a number (`Number(value)` is `NaN`, e.g.
 * `'abc'`) compiles to `null` in the real compiler, but this function has no
 * field-type information (only `CONTACT_SIGNAL_FIELD_KEYS`, not a
 * `FilterFieldMap`) to reproduce that one type-specific check, so it reports
 * active (matching neither more nor less accurately than the version this
 * replaces, which had the identical gap). A garbage string is a vanishingly
 * unlikely value for a hidden numeric-rank field's filter UI.
 *
 * Deliberately simpler than `conditionValueNarrowsSignals`: this asks "is a
 * predicate present at all", not "does it narrow the Signal lateral" — it
 * does NOT apply the `signal_name` arm-scaffolding check
 * (`CAMPAIGN_SIGNAL_NAME_INVARIANT_PREFIX`), because a `contains 'Campaign'`
 * condition is still a real, compiled Signal predicate even though it
 * happens not to narrow anything.
 */
export function conditionIsActive(cond: FilterCondition): boolean {
  if (NO_VALUE_OPERATORS.has(cond.operator)) return true;
  return cond.value != null;
}

/**
 * True when `cond` alone narrows the Signal lateral's candidate set (see
 * `SIGNAL_NARROWING_FIELDS`). `isSignalField` defaults to
 * `defaultIsSignalField`; the server wrapper
 * (`contact-signal-columns.ts#hasNarrowingSignalCondition`) passes its own
 * `FilterFieldMap`-backed predicate instead.
 */
export function conditionNarrowsSignals(
  cond: FilterCondition,
  isSignalField: IsSignalField = defaultIsSignalField,
): boolean {
  return isSignalField(cond.field)
    && SIGNAL_NARROWING_FIELDS.includes(cond.field)
    && NARROWING_OPERATORS.has(cond.operator)
    && conditionValueNarrowsSignals(cond);
}

/**
 * True when `group` narrows, given its OWN `logic`: an `AND` group narrows
 * if ANY condition narrows (one selective predicate is enough to bound the
 * group's contribution); an `OR` group narrows only if EVERY condition
 * narrows (a single non-narrowing branch reopens the whole set). A group
 * with zero conditions never narrows — `filterGroupsToSql`'s own
 * `activeGroups` filter is mirrored by the caller, not duplicated here, so
 * this stays the single-group predicate.
 */
export function groupNarrowsSignals(
  group: FilterGroup,
  isSignalField: IsSignalField = defaultIsSignalField,
): boolean {
  if (group.conditions.length === 0) return false;
  return group.logic === 'AND'
    ? group.conditions.some(c => conditionNarrowsSignals(c, isSignalField))
    : group.conditions.every(c => conditionNarrowsSignals(c, isSignalField));
}

/**
 * True when the WHOLE request narrows the Signal lateral's input — the
 * server's `rejectUnsortableField` guard's "narrowed" half (`flow.ts`, U3)
 * and the frontend's "already narrowed, sort/group immediately" check (U5).
 * Same AND/OR combination rule as `groupNarrowsSignals`, applied one level up
 * across the request's own `group_logic`: `AND` narrows if SOME active
 * group narrows, `OR` narrows only if EVERY active group narrows. Empty or
 * all-empty-conditions input never narrows.
 */
export function requestNarrowsSignals(
  groups: FilterGroup[],
  groupLogic: GroupLogic,
  isSignalField: IsSignalField = defaultIsSignalField,
): boolean {
  const activeGroups = groups.filter(g => g.conditions.length > 0);
  if (activeGroups.length === 0) return false;
  return groupLogic === 'AND'
    ? activeGroups.some(g => groupNarrowsSignals(g, isSignalField))
    : activeGroups.every(g => groupNarrowsSignals(g, isSignalField));
}

/**
 * D9 (Item C) — "warn, don't merge": the number of ACTIVE groups (non-empty
 * `conditions`) that contain at least one Signal-field condition that
 * compiles to a real predicate per `conditionIsActive`'s rule (NOT the
 * narrower `conditionValueNarrowsSignals` — see that function's own doc for
 * why they differ). `ContactFlowTable`
 * renders a warning above the table when this is `> 1` ("These Signal
 * filters are in different groups, so they may match different signals.") —
 * a split-group Signal filter is legal SQL (each group's own `logic`
 * combines fine across an outer AND/OR), but it is easy for a rep to build
 * by accident and hard to notice is matching two different signals.
 *
 * Deliberately NOT gated by `SIGNAL_NARROWING_FIELDS`/`NARROWING_OPERATORS`
 * the way `conditionNarrowsSignals` is — a Signal condition on ANY Signal
 * field (not just the two narrowing fields) that compiles to a real
 * predicate counts here (including a whitespace-only `contains`/
 * `not_contains` value or an empty `in_any_of`/`not_in_any_of` array — the
 * real compiler still emits a predicate for both, see `conditionIsActive`),
 * and a group's own `logic` does not matter either: "one Signal
 * condition present" is enough for that group to count, unlike narrowing
 * where an OR group needs EVERY condition to narrow. The two questions
 * ("does this bound the lateral" vs. "is a Signal filter present here at
 * all, and might it disagree with a Signal filter in another group") are
 * different, so they use different predicates.
 *
 * Uses `conditionIsActive` (U5, 2-2), not `conditionValueNarrowsSignals` —
 * the latter is the value-shape half of the NARROWING question (rejects
 * `is_empty`/`is_not_empty`/`before_today` outright, and rejects a
 * `signal_name contains` value that's a substring of the Campaign arm's
 * invariant prefix) and so undercounts here: a rep's `signal_status
 * is_not_empty` group, or a `signal_name contains 'Campaign'` group, IS a
 * real Signal predicate that could disagree with a Signal predicate in
 * another group, even though neither one narrows the lateral. Defaults to
 * `defaultIsSignalConditionField`, not `defaultIsSignalField` — the default
 * caller (`ContactFlowTable.tsx`) needs the `filterOnly` Signal fields
 * (`campaign_playhouse_visible`) counted too.
 */
export function countSignalConditionGroups(
  groups: FilterGroup[],
  isSignalField: IsSignalField = defaultIsSignalConditionField,
): number {
  return groups
    .filter(g => g.conditions.length > 0)
    .filter(g => g.conditions.some(c => isSignalField(c.field) && conditionIsActive(c)))
    .length;
}
